/**
 * Engine — the simulation core. `tickDay()` advances the world one day;
 * pure mutation of a (cloned) GameState. All randomness enters through
 * per-day seeded streams so runs are reproducible from `meta.seed`.
 *
 * Tick order matters:
 *   1. time & world        5. sales & returns
 *   2. expiries/arrivals   6. cash application & P&L accumulation
 *   3. R&D completions     7. rank/awareness/reputation dynamics
 *   4. marketing           8. quarterly close, events, offers, achievements
 */

import {
  ACHIEVEMENTS,
  BANKRUPTCY_FLOOR,
  BASE_OVERHEAD_DAILY,
  BASE_WAREHOUSE_CAPACITY,
  CAPACITY_UPGRADE_COST_STEP,
  CAPACITY_UPGRADE_STEP,
  CREDIT_APR_DAILY,
  DAYS_PER_QUARTER,
  DIFFICULTIES,
  INITIAL_TARIFFS,
  MAX_PRODUCTS,
  QUARTERLY_TAX_RATE,
  RESEARCH_TREE,
  SHOWROOM_CITIES,
  SHOWROOM_DAILY_COST,
  SHOWROOM_MAX_LOCATIONS,
  SHOWROOM_OPEN_COST,
  SHOWROOM_UPGRADE_COST,
  SHOWROOM_WHOLESALE_BONUS,
  SUPPLIERS,
  TEAM_HIRES,
  TUNING,
} from './constants';
import { clamp, formatGameDate, money, quarterLabel } from './format';
import {
  activeProducts,
  getMods,
  landedUnitCost,
  runDailySales,
  runMarketing,
} from './economy';
import { archetype, blendedAsp, createProduct, targetRating } from './products';
import { has, researchById, researchDays } from './research';
import { gauss, randInt, rngForDay } from './rng';
import {
  EVENT_BY_ID,
  findScriptedEvent,
  fireEvent,
  pushMod,
  resolvePendingEvent,
  rollRandomEvent,
} from './events';
import { SAVE_VERSION } from './save';
import {
  growMarket,
  initialWorld,
  seasonalMultiplier,
  stepCompetition,
  stepFoamIndex,
  stepOil,
  updateOilLagged,
} from './world';
import type {
  Difficulty,
  GameState,
  MarketingAllocation,
  NewsItem,
  Product,
  PurchaseOrder,
  QuarterlyReport,
} from './types';

// ---------------------------------------------------------------------------
// New game
// ---------------------------------------------------------------------------

export interface NewGameOptions {
  companyName: string;
  difficulty: Difficulty;
  seed?: number;
}

export function createNewGame(opts: NewGameOptions): GameState {
  const seed = opts.seed ?? Math.floor(Math.random() * 0xffffffff) >>> 0;
  const preset = DIFFICULTIES[opts.difficulty];
  const { world, oilHistory } = initialWorld(opts.difficulty, seed);
  const companyName = sanitizeCompanyName(opts.companyName);

  // Starter SKU — the classic founder move: one all-foam bed-in-a-box.
  const a = archetype('allFoam');
  const starterQuality = 55;
  const starterBaseCost = Math.round(
    a.minQualityCost + ((starterQuality - 40) / 55) * (a.maxQualityCost - a.minQualityCost),
  );
  const starter: Product = {
    id: 'sku_starter',
    name: `${companyName.split(' ')[0]} Classic`,
    archetype: 'allFoam',
    status: 'active',
    devCompleteDay: null,
    price: 549,
    quality: starterQuality,
    baseFactoryCost: starterBaseCost,
    landedCost: Math.round(starterBaseCost * INITIAL_TARIFFS.CN + 22 + 7),
    coolingScore: 38,
    returnRateBase: a.returnRateBase,
    reviews: { rating: 4.3, count: 14 },
    unitsSoldTotal: 0,
    returnsTotal: 0,
  };

  const allocation: MarketingAllocation = {
    meta: 0.35,
    google: 0.2,
    tiktok: 0.15,
    influencer: 0.1,
    amazonAds: 0.15,
    email: 0.05,
  };

  const s: GameState = {
    meta: {
      version: SAVE_VERSION,
      seed,
      createdAt: Date.now(),
      lastPlayed: Date.now(),
      difficulty: opts.difficulty,
    },
    player: {
      companyName,
      cash: preset.startingCash,
      creditDrawn: 0,
      reputation: 55,
      awareness: 6,
    },
    time: { day: 0, quarter: 1, year: 2024 },
    world,
    oilHistory,
    tariffs: { ...INITIAL_TARIFFS },
    products: [starter],
    inventory: { [starter.id]: 320 },
    pos: [],
    suppliersUnlocked: ['foshan-foamworks', 'binh-duong-sleep'],
    warehouseExpansions: 0,
    showrooms: [],
    channels: {
      amazon: {
        enabled: true,
        sellerRating: 88,
        organicRankScore: 8,
        reviewHealth: 70,
        feePercent: 0.15,
        fulfillmentPerUnit: 74,
        suspended: false,
      },
      shopify: {
        enabled: true,
        // BALANCE (v2): 0.014 → 0.017 baseline (better default site UX).
        conversionRateBase: 0.017,
        paymentFeePercent: 0.032,
        shippingPerUnitBase: 94,
      },
      wholesale: {
        enabled: false,
        discountOffRetail: 0.45,
        receivables: 0,
        nextCollectionDay: Number.MAX_SAFE_INTEGER,
        quarterlyDemandUnits: 260,
        contractRetailers: 0,
      },
    },
    marketing: {
      enabled: true,
      dailyBudget: 220,
      allocation,
      fatigue: { meta: 0, google: 0, tiktok: 0, influencer: 0, amazonAds: 0, email: 0 },
      emailListSize: 240,
      returningCustomerPool: 150,
    },
    finance: {
      creditLimit: preset.creditLimit,
      aprDaily: CREDIT_APR_DAILY,
      taxRateQuarterly: QUARTERLY_TAX_RATE,
      fixedOverheadDaily: BASE_OVERHEAD_DAILY,
      qAccum: emptyAccum(),
      lossCarryforward: 0,
    },
    team: { hires: TEAM_HIRES.map((h) => ({ ...h })), overheadDaily: BASE_OVERHEAD_DAILY },
    modifiers: [],
    activeEvents: [],
    pendingEventId: null,
    eventCooldowns: {},
    firedOnceEvents: [],
    news: [
      {
        id: 'n_welcome',
        day: 0,
        category: 'milestone',
        severity: 1,
        headline: `${companyName} launches its first bed-in-a-box`,
        detail: `One SKU live on Amazon + Shopify, ${money(preset.startingCash)} in the bank. 175+ brands want your lunch. Good luck.`,
      },
    ],
    research: { completed: [], inProgress: null },
    achievements: [],
    history: { daily: [], quarterly: [] },
    stats: {
      totalRevenue: 0,
      totalUnits: 0,
      totalReturns: 0,
      totalAdSpend: 0,
      bestDailyRevenue: 0,
      quartersProfitable: 0,
      peakCash: preset.startingCash,
    },
    exitOffer: null,
    gameOver: null,
  };
  return s;
}

function emptyAccum() {
  return {
    revenue: 0,
    cogs: 0,
    marketing: 0,
    channelFees: 0,
    shipping: 0,
    returnsCost: 0,
    overhead: 0,
    interest: 0,
    tax: 0,
    netIncome: 0,
    unitsSold: 0,
    unitsReturned: 0,
    newCustomers: 0,
    adSpendByChannel: { meta: 0, google: 0, tiktok: 0, influencer: 0, amazonAds: 0, email: 0 },
  };
}

/** Player-supplied names are sanitized everywhere they enter state. */
export function sanitizeCompanyName(raw: string): string {
  const cleaned = raw.replace(/[<>&"'`\\{}[\]$;]/g, '').replace(/\s+/g, ' ').trim().slice(0, 32);
  return cleaned.length > 0 ? cleaned : 'DreamCo';
}

// ---------------------------------------------------------------------------
// Warehouse helpers
// ---------------------------------------------------------------------------

export function warehouseCapacity(s: GameState): number {
  return BASE_WAREHOUSE_CAPACITY + s.warehouseExpansions * CAPACITY_UPGRADE_STEP;
}

export function unitsOnOrder(s: GameState): number {
  return s.pos.reduce((a, po) => a + po.units, 0);
}

export function totalInventory(s: GameState): number {
  let t = 0;
  for (const v of Object.values(s.inventory)) t += v ?? 0;
  return t;
}

// ---------------------------------------------------------------------------
// Player actions (mutate state; callers clone before invoking)
// ---------------------------------------------------------------------------

export function actionPlacePO(
  s: GameState,
  productId: string,
  supplierId: string,
  units: number,
): string | null {
  const sup = SUPPLIERS.find((d) => d.id === supplierId);
  if (!sup) return 'Unknown supplier.';
  if (!s.suppliersUnlocked.includes(supplierId)) return 'Supplier not unlocked.';
  const p = s.products.find((x) => x.id === productId && x.status === 'active');
  if (!p) return 'Product not found or inactive.';
  if (!Number.isFinite(units) || units < sup.minOrderUnits) {
    return `Minimum order is ${sup.minOrderUnits.toLocaleString()} units.`;
  }
  const capacity = warehouseCapacity(s);
  if (totalInventory(s) + unitsOnOrder(s) + units > capacity) {
    return `Warehouse capacity exceeded (${capacity.toLocaleString()} units). Expand storage or sell stock first.`;
  }
  const unitEstimate = landedUnitCost(s, p, supplierId);
  const cost = unitEstimate * units;
  if (s.player.cash < cost) return `Insufficient cash — need ${money(cost)}.`;

  s.player.cash -= cost; // inventory paid NOW — the classic DTC cash trap

  // Reliability-driven delay variance rolled at placement (keeps determinism).
  const rng = rngForDay(s.meta.seed, s.time.day, 77);
  let delay = 0;
  const delayChance =
    (1 - sup.reliability) * (s.team.hires.find((h) => h.id === 'supply-manager')?.hired ? 0.7 : 1);
  if (rng() < delayChance) delay = randInt(rng, 4, 14);

  const po: PurchaseOrder = {
    id: `po_${s.time.day}_${Math.floor(rng() * 1e9).toString(36)}`,
    productId,
    supplierId,
    units,
    placedDay: s.time.day,
    etaDay: s.time.day + sup.leadTimeDays + delay,
    unitCostEstimate: Math.round(unitEstimate),
    paid: true,
  };
  s.pos.push(po);
  addNews(s, 'finance', 1, `PO placed: ${units.toLocaleString()} × ${p.name}`, `${sup.countryName} • ETA ${formatGameDate(po.etaDay)} • ${money(cost)} paid up front.`);
  return null;
}

export function actionSetPrice(s: GameState, productId: string, price: number): void {
  const p = s.products.find((x) => x.id === productId);
  if (p) p.price = clamp(Math.round(price), 149, 3999);
}

export function actionDiscontinueProduct(s: GameState, productId: string): void {
  const p = s.products.find((x) => x.id === productId);
  if (p && s.products.filter((x) => x.status === 'active').length > 1) {
    p.status = 'discontinued';
    addNews(s, 'milestone', 1, `${p.name} discontinued`, 'Listings archived. Remaining inventory sells through Amazon clearance.');
  }
}

export function actionDesignProduct(
  s: GameState,
  spec: { name: string; archetypeId: Product['archetype']; quality: number; price: number },
): string | null {
  if (s.products.filter((p) => p.status !== 'discontinued').length >= MAX_PRODUCTS) {
    return `Maximum ${MAX_PRODUCTS} concurrent SKUs — discontinue one first.`;
  }
  const a = archetype(spec.archetypeId);
  if (a.requiresResearch && !has(s, a.requiresResearch)) return 'Complete the required research first.';
  if (s.player.cash < a.devCost) return `Development requires ${money(a.devCost)}.`;
  s.player.cash -= a.devCost;
  const product = createProduct(s, {
    name: spec.name,
    archetype: spec.archetypeId,
    quality: spec.quality,
    price: spec.price,
  });
  s.products.push(product);
  s.inventory[product.id] = 0;
  addNews(s, 'milestone', 1, `R&D started: ${product.name}`, `Sampling & certification ~${a.devDays} days.`);
  return null;
}

export function actionStartResearch(s: GameState, nodeId: string): string | null {
  if (s.research.inProgress) return 'A project is already underway.';
  if (has(s, nodeId)) return 'Already completed.';
  const node = researchById(nodeId);
  if (!node) return 'Unknown research node.';
  if (!node.prereqs.every((p) => has(s, p))) return 'Prerequisites missing.';
  if (s.player.cash < node.cost) return `Requires ${money(node.cost)}.`;
  s.player.cash -= node.cost;
  const days = researchDays(s, node.rDays);
  s.research.inProgress = { nodeId, completeDay: s.time.day + days };
  addNews(s, 'milestone', 1, `Research started: ${node.name}`, `~${days} days to completion.`);
  return null;
}

export function actionHire(s: GameState, hireId: string): string | null {
  const h = s.team.hires.find((x) => x.id === hireId);
  if (!h || h.hired) return 'Unavailable.';
  h.hired = true;
  recomputeOverhead(s);
  addNews(s, 'finance', 1, `Hired: ${h.name}`, h.effectText);
  return null;
}

export function recomputeOverhead(s: GameState): void {
  let oh = BASE_OVERHEAD_DAILY;
  for (const h of s.team.hires) if (h.hired) oh += h.dailyCost;
  for (const r of s.showrooms ?? []) oh += SHOWROOM_DAILY_COST[r.tier];
  s.team.overheadDaily = oh;
}

export function actionSetMarketing(
  s: GameState,
  budget: number,
  allocation: MarketingAllocation,
  enabled: boolean,
): void {
  s.marketing.dailyBudget = clamp(Math.round(budget), 0, 100_000);
  const sum = Object.values(allocation).reduce((a, b) => a + clamp(b, 0, 1), 0);
  if (sum > 1.0001) {
    for (const k of Object.keys(allocation) as (keyof MarketingAllocation)[]) {
      allocation[k] = allocation[k] / sum;
    }
  }
  s.marketing.allocation = allocation;
  s.marketing.enabled = enabled && s.marketing.dailyBudget > 0;
}

export function actionDrawCredit(s: GameState, amount: number): string | null {
  const room = s.finance.creditLimit - s.player.creditDrawn;
  const amt = clamp(Math.round(amount), 0, room);
  if (amt <= 0) return 'No credit available.';
  s.player.creditDrawn += amt;
  s.player.cash += amt;
  addNews(s, 'finance', 1, `Drew ${money(amt)} on credit line`, `Balance ${money(s.player.creditDrawn)} @ ${(s.finance.aprDaily * 365 * 100).toFixed(1)}% APR.`);
  return null;
}

export function actionRepayCredit(s: GameState, amount: number): string | null {
  const amt = clamp(Math.round(amount), 0, Math.min(s.player.cash, s.player.creditDrawn));
  if (amt <= 0) return 'Nothing to repay.';
  s.player.creditDrawn -= amt;
  s.player.cash -= amt;
  return null;
}

export function actionExpandWarehouse(s: GameState): string | null {
  const cost = CAPACITY_UPGRADE_COST_STEP * (s.warehouseExpansions + 1);
  if (s.player.cash < cost) return `Expansion costs ${money(cost)}.`;
  s.player.cash -= cost;
  s.warehouseExpansions += 1;
  addNews(s, 'finance', 1, 'Warehouse expanded', `Capacity now ${warehouseCapacity(s).toLocaleString()} units.`);
  return null;
}

// ---------------------------------------------------------------------------
// Showrooms
// ---------------------------------------------------------------------------

export function actionOpenShowroom(s: GameState): string | null {
  if (!has(s, 'showroom-pilot')) return 'Complete the Showroom Pilot Program first.';
  if ((s.showrooms?.length ?? 0) >= SHOWROOM_MAX_LOCATIONS) {
    return `Maximum ${SHOWROOM_MAX_LOCATIONS} locations — remodel existing ones instead.`;
  }
  if (s.player.cash < SHOWROOM_OPEN_COST) return `Opening a location costs ${money(SHOWROOM_OPEN_COST)}.`;
  const used = new Set((s.showrooms ?? []).map((r) => r.city));
  const nextCity = SHOWROOM_CITIES.find((c) => !used.has(c.city));
  if (!nextCity) return 'No remaining markets.';
  s.player.cash -= SHOWROOM_OPEN_COST;
  s.showrooms.push({ id: `room_${s.time.day}_${Math.floor(rngForDay(s.meta.seed, s.time.day, 55)() * 1e9).toString(36)}`, city: nextCity.city, tier: 1, openedDay: s.time.day });
  recomputeOverhead(s);
  addNews(s, 'milestone', 2, `Showroom opened: ${nextCity.city}`, nextCity.blurb);
  return null;
}

export function actionUpgradeShowroom(s: GameState, id: string): string | null {
  const room = s.showrooms?.find((r) => r.id === id);
  if (!room) return 'Location not found.';
  if (room.tier >= 3) return 'Already flagship tier.';
  const nextTier = (room.tier + 1) as 2 | 3;
  const cost = SHOWROOM_UPGRADE_COST[nextTier];
  if (s.player.cash < cost) return `Remodel costs ${money(cost)}.`;
  s.player.cash -= cost;
  room.tier = nextTier;
  recomputeOverhead(s);
  addNews(s, 'milestone', 1, `${room.city} remodeled to Tier ${nextTier}`, 'Bigger floor, better sleep-bar experience.');
  return null;
}

export function actionResolveEvent(s: GameState, choiceId: string | null): string | null {
  const rng = rngForDay(s.meta.seed, s.time.day, 999);
  return resolvePendingEvent(s, choiceId, rng);
}

export function actionAcceptExit(s: GameState): void {
  if (!s.exitOffer) return;
  const o = s.exitOffer;
  s.gameOver = {
    reason: 'acquired',
    day: s.time.day,
    headline: `${o.acquirer} acquires ${s.player.companyName} for ${money(o.valuation)}`,
    summary: buildPostMortem(s, o.valuation),
    valuation: o.valuation,
  };
  s.exitOffer = null;
}

export function actionDeclineExit(s: GameState): void {
  s.exitOffer = null;
  s.eventCooldowns['__exit_offer'] = s.time.day + TUNING.exitOfferIntervalDays;
}

function buildPostMortem(s: GameState, exitValue?: number): string[] {
  const lines: string[] = [];
  lines.push(`Days operated: ${s.time.day}`);
  lines.push(`Lifetime revenue: ${money(s.stats.totalRevenue)}`);
  lines.push(`Units sold: ${Math.round(s.stats.totalUnits).toLocaleString()} (returns: ${Math.round(s.stats.totalReturns).toLocaleString()})`);
  lines.push(`Total ad spend: ${money(s.stats.totalAdSpend)}`);
  lines.push(`Profitable quarters: ${s.stats.quartersProfitable}`);
  lines.push(`Peak cash: ${money(s.stats.peakCash)}`);
  if (exitValue) lines.push(`Exit value: ${money(exitValue)}`);
  lines.push(`Final cash: ${money(s.player.cash)}`);
  return lines;
}

// ---------------------------------------------------------------------------
// Daily tick
// ---------------------------------------------------------------------------

export interface TickOptions {
  /** Suppress new events & auto-skip modals (offline catch-up simulation). */
  suppressEvents?: boolean;
}

export function tickDay(s: GameState, opts: TickOptions = {}): void {
  if (s.gameOver) return;
  const day = ++s.time.day;
  const preset = DIFFICULTIES[s.meta.difficulty];

  // 1 ─ World ---------------------------------------------------------------
  stepOil(s.world, rngForDay(s.meta.seed, day, 1), preset.oilVolatility);
  s.oilHistory.push(s.world.oil);
  if (s.oilHistory.length > 400) s.oilHistory.splice(0, s.oilHistory.length - 400);
  updateOilLagged(s.world, s.oilHistory, day);
  stepFoamIndex(s.world);
  stepCompetition(s.world, rngForDay(s.meta.seed, day, 2), preset.competitionBase);
  growMarket(s.world);

  // Expire modifiers.
  s.modifiers = s.modifiers.filter((m) => m.expiresDay >= day);

  // Amazon suspension marker auto-reinstatement.
  const unsuspIdx = s.activeEvents.findIndex((a) => a.defId === '__amazon_unsuspend');
  if (unsuspIdx >= 0) {
    if (day > s.activeEvents[unsuspIdx].expiresDay) {
      s.channels.amazon.suspended = false;
      s.activeEvents.splice(unsuspIdx, 1);
      addNews(s, 'platform', 1, 'Amazon account reinstated', 'Sellings resume. Keep an eye on Account Health.');
    }
  }
  // Prune expired event records.
  s.activeEvents = s.activeEvents.filter((a) => a.expiresDay >= day);

  if (day % DAYS_PER_QUARTER === 1) {
    const q = quarterLabel(day);
    s.time.quarter = q.quarter;
    s.time.year = q.year;
  }

  // 2 ─ PO arrivals -----------------------------------------------------------
  for (const po of s.pos.slice()) {
    if (po.etaDay > day) continue;
    arrivePO(s, po);
    s.pos = s.pos.filter((x) => x.id !== po.id);
  }

  // 3 ─ R&D completions ---------------------------------------------------------
  for (const p of s.products) {
    if (p.status === 'development' && p.devCompleteDay !== null && p.devCompleteDay <= day) {
      p.status = 'active';
      p.devCompleteDay = null;
      addNews(s, 'milestone', 2, `${p.name} is live`, 'Listings published across channels.');
    }
  }
  if (s.research.inProgress && s.research.inProgress.completeDay <= day) {
    const node = researchById(s.research.inProgress.nodeId);
    if (node) {
      s.research.completed.push(node.id);
      s.research.inProgress = null;
      for (const sup of SUPPLIERS) {
        if (sup.requiresResearch === node.id && !s.suppliersUnlocked.includes(sup.id)) {
          s.suppliersUnlocked.push(sup.id);
        }
      }
      if (node.id === 'wholesale-program') enableWholesale(s);
      if (node.id === 'brand-refresh') s.player.awareness = clamp(s.player.awareness + 8, 0, 100);
      if (node.id === 'international') {
        s.modifiers.push({
          id: `mod_${day}_intl`, field: 'demandMult', mult: 1.18, expiresDay: 999999, label: 'International demand pool (CA/EU)',
        });
      }
      if (node.id === 'showroom-pilot') recomputeOverhead(s);
      addNews(s, 'milestone', 2, `Research complete: ${node.name}`, node.effectText);
    } else {
      s.research.inProgress = null;
    }
  }

  // 4 ─ Marketing -----------------------------------------------------------------
  const mk = runMarketing(s, rngForDay(s.meta.seed, day, 3));
  if (mk.viralTriggered) {
    pushMod(s, 'demandMult', { mult: 1.15, days: 10, label: 'TikTok moment', source: 'tiktok-viral' });
    pushMod(s, 'awarenessAddPerDay', { add: 0.8, days: 10, label: 'TikTok virality' });
    addNews(s, 'viral', 2, 'Your TikTok content caught fire', 'A clip is racking up views. Demand spike incoming.');
  }

  // 5 ─ Sales ----------------------------------------------------------------------
  const sales = runDailySales(s, rngForDay(s.meta.seed, day, 4), mk);

  // 6 ─ Cash application & P&L accumulation --------------------------------------------
  const overhead = s.team.overheadDaily + getMods(s, 'overheadAdd').add;
  const interest = s.player.creditDrawn * s.finance.aprDaily;
  const feesTotal = sales.amazonFees + sales.shopifyFees;

  const cashDelta =
    sales.revenue -
    sales.refundLoss -
    sales.returnsCost -
    feesTotal -
    mk.totalSpend -
    overhead -
    sales.holdingCost -
    interest;

  s.player.cash += cashDelta;

  // Wholesale receivables collection.
  const wh = s.channels.wholesale;
  if (wh.enabled && wh.receivables > 0 && day >= wh.nextCollectionDay) {
    s.player.cash += wh.receivables;
    addNews(s, 'finance', 1, 'Wholesale receivables collected', `${money(wh.receivables)} lands. Net-60 finally pays.`);
    wh.receivables = 0;
    wh.nextCollectionDay = Number.MAX_SAFE_INTEGER;
  }

  const qa = s.finance.qAccum;
  qa.revenue += sales.revenue - sales.refundLoss;
  qa.cogs += sales.cogsExpense;
  qa.marketing += mk.totalSpend;
  qa.channelFees += feesTotal;
  qa.returnsCost += sales.returnsCost;
  qa.overhead += overhead + sales.holdingCost;
  qa.interest += interest;
  qa.netIncome +=
    sales.revenue - sales.refundLoss - sales.cogsExpense - sales.returnsCost -
    feesTotal - mk.totalSpend - overhead - sales.holdingCost - interest;
  qa.unitsSold += sales.units;
  qa.unitsReturned += sales.returnsUnits;
  qa.newCustomers += sales.newCustomers;
  for (const ch of Object.keys(qa.adSpendByChannel) as (keyof typeof qa.adSpendByChannel)[]) {
    qa.adSpendByChannel[ch] += mk.perChannel[ch]?.spend ?? 0;
  }

  s.stats.totalRevenue += sales.revenue - sales.refundLoss;
  s.stats.totalUnits += sales.units;
  s.stats.totalReturns += sales.returnsUnits;
  s.stats.totalAdSpend += mk.totalSpend;
  s.stats.bestDailyRevenue = Math.max(s.stats.bestDailyRevenue, sales.revenue);
  s.stats.peakCash = Math.max(s.stats.peakCash, s.player.cash);

  s.history.daily.push({
    d: day,
    rev: Math.round(sales.revenue),
    net: Math.round(cashDelta),
    cash: Math.round(s.player.cash),
    units: sales.units,
  });
  if (s.history.daily.length > 180) s.history.daily.splice(0, s.history.daily.length - 180);

  // 7 ─ Dynamics -----------------------------------------------------------------------
  updateDynamics(s, rngForDay(s.meta.seed, day, 5), sales.amazonUnits, sales.stockouts);

  // 8 ─ Quarter close --------------------------------------------------------------------
  if (day % DAYS_PER_QUARTER === 0) closeQuarter(s, day);

  // Events ---------------------------------------------------------------------------------
  if (opts.suppressEvents) {
    if (s.pendingEventId) {
      const def = EVENT_BY_ID[s.pendingEventId];
      if (def?.choices?.length) {
        addNews(s, 'opportunity', 2, 'Decision auto-skipped while away', `"${def.title}" resolved with no action taken.`);
      }
      s.pendingEventId = null;
    }
  } else if (!s.pendingEventId) {
    const scripted = findScriptedEvent(s);
    const def = scripted ?? rollRandomEvent(s, rngForDay(s.meta.seed, day, 6), preset.eventSeverityMult);
    if (def) fireEvent(s, def, rngForDay(s.meta.seed, day, 7));
  }

  maybeExitOffer(s);
  checkAchievements(s, sales.units > 0);

  if (s.player.cash < BANKRUPTCY_FLOOR) {
    s.gameOver = {
      reason: 'bankrupt',
      day,
      headline: `${s.player.companyName} runs out of runway`,
      summary: buildPostMortem(s),
    };
  }
  s.meta.lastPlayed = Date.now();
}

// ---------------------------------------------------------------------------
// Sub-systems
// ---------------------------------------------------------------------------

function arrivePO(s: GameState, po: PurchaseOrder): void {
  const p = s.products.find((x) => x.id === po.productId);
  const sup = SUPPLIERS.find((d) => d.id === po.supplierId);
  if (!p || !sup) return;

  // Landed cost is computed at ARRIVAL under current tariffs/oil/foam —
  // real invoices reflect the world as it is, not as it was when ordered.
  const unitCost = landedUnitCost(s, p, po.supplierId);
  const defectRate = sup.qualityVariance * (has(s, 'in-house-machinery') ? 0.6 : 1);
  const scrapped = Math.floor(po.units * defectRate);
  const goodUnits = po.units - scrapped;

  const invOld = s.inventory[p.id] ?? 0;
  const totalGood = invOld + goodUnits;
  if (totalGood > 0) {
    const totalCost = invOld * p.landedCost + po.units * unitCost; // scrap cost absorbed into average
    p.landedCost = Math.round((totalCost / totalGood) * 100) / 100;
  }
  s.inventory[p.id] = totalGood;

  if (scrapped > po.units * 0.06) {
    addNews(s, 'supply', 2, `QC rejected ${scrapped.toLocaleString()} units`, `${sup.name} batch arrived with elevated defects.`);
  }
}

function enableWholesale(s: GameState): void {
  s.channels.wholesale.enabled = true;
  s.channels.wholesale.contractRetailers = 6;
}

/** Rank flywheel, seller rating, awareness decay, reputation drift, reviews. */
function updateDynamics(s: GameState, rng: () => number, amazonUnitsToday: number, stockout: boolean): void {
  const amz = s.channels.amazon;

  const oosPenalty = stockout ? (s.team.hires.find((h) => h.id === 'supply-manager')?.hired ? 0.8 : 1.6) : 0;
  // BALANCE (v2): velocity coefficient 0.22 → 0.25 and decay slope
  // 0.095 → 0.10 — raises the velocity↔rank equilibrium (~2.5×units) so the
  // Amazon flywheel rewards momentum sooner without changing the ceiling.
  const gain = 0.25 * amazonUnitsToday - 0.10 * amz.organicRankScore - 0.15 - oosPenalty;
  amz.organicRankScore = clamp(amz.organicRankScore + gain + gauss(rng) * 0.12, 1, 100);

  const rr = blendedReturnRateApprox(s);
  const sellerTarget = clamp(96 - rr * 180, 45, 97);
  amz.sellerRating += (sellerTarget - amz.sellerRating) * 0.01;

  const mods = getMods(s, 'awarenessAddPerDay');
  s.player.awareness = clamp(
    s.player.awareness + mods.add - TUNING.awarenessDecayPerDay * (s.player.awareness / 25),
    0,
    100,
  );

  const csAgent = s.team.hires.find((h) => h.id === 'cs-agent')?.hired;
  const repTarget =
    TUNING.reputationDriftTarget +
    (csAgent ? 8 : 0) +
    (has(s, 'service-playbook') ? 6 : 0) +
    Math.min(8, (s.showrooms?.length ?? 0) * 2) - // walk-in retail trust halo
    Math.max(0, rr - 0.12) * 160;
  s.player.reputation = clamp(s.player.reputation + (repTarget - s.player.reputation) * TUNING.reputationDriftRate, 0, 100);

  for (const p of activeProducts(s)) {
    const t = targetRating(s, p);
    p.reviews.rating = clamp(p.reviews.rating + (t - p.reviews.rating) * 0.02 + gauss(rng) * 0.008, 1.5, 5.0);
  }
}

function blendedReturnRateApprox(s: GameState): number {
  const act = activeProducts(s);
  if (!act.length) return 0.12;
  const base = act.reduce((a, p) => a + p.returnRateBase - ((p.quality - 50) / 100) * 0.06, 0) / act.length;
  return clamp(base, 0.04, 0.28);
}

function closeQuarter(s: GameState, day: number): void {
  const qa = s.finance.qAccum;
  const q = quarterLabel(day - 1); // the quarter that just ended

  let taxable = qa.netIncome - s.finance.lossCarryforward;
  let tax = 0;
  if (taxable > 0) {
    tax = taxable * s.finance.taxRateQuarterly;
    s.finance.lossCarryforward = 0;
  } else {
    s.finance.lossCarryforward = Math.min(500_000, s.finance.lossCarryforward + -taxable);
    taxable = 0;
  }
  s.player.cash -= tax;
  qa.tax = tax;
  qa.netIncome -= tax;

  const report: QuarterlyReport = {
    year: q.year,
    quarter: q.quarter,
    revenue: qa.revenue,
    cogs: qa.cogs,
    grossProfit: qa.revenue - qa.cogs,
    marketing: qa.marketing,
    channelFees: qa.channelFees,
    shipping: qa.shipping,
    returnsCost: qa.returnsCost,
    overhead: qa.overhead,
    interest: qa.interest,
    tax,
    netIncome: qa.netIncome,
    unitsSold: qa.unitsSold,
    returnRate: qa.unitsSold > 0 ? qa.unitsReturned / qa.unitsSold : 0,
    blendedCac: qa.newCustomers > 0 ? qa.marketing / qa.newCustomers : 0,
    endingCash: s.player.cash,
    notes: [],
  };
  if (qa.netIncome > 0) {
    s.stats.quartersProfitable += 1;
    report.notes.push('Profitable quarter — rare air in this category.');
  }
  s.history.quarterly.push(report);
  if (s.history.quarterly.length > 24) s.history.quarterly.shift();

  addNews(
    s,
    'finance',
    qa.netIncome >= 0 ? 1 : 2,
    `${q.label} closed: net ${money(qa.netIncome)}`,
    `Revenue ${money(qa.revenue)} • CAC ${report.blendedCac > 0 ? money(report.blendedCac) : '—'} • Returns ${(report.returnRate * 100).toFixed(1)}%`,
  );

  // Wholesale quarterly bulk order.
  const wh = s.channels.wholesale;
  if (wh.enabled) {
    const season = seasonalMultiplier(day);
    const repFactor = 0.6 + s.player.reputation / 125;
    const qualityFactor = 0.7 + averageQualityApprox(s) / 200;
    // Showrooms put floor models in front of regional retailers.
    const showroomFactor = 1 + SHOWROOM_WHOLESALE_BONUS * (s.showrooms?.length ?? 0);
    const units = Math.round(wh.quarterlyDemandUnits * repFactor * qualityFactor * season * showroomFactor);
    const act = activeProducts(s);
    const totalInv = act.reduce((acc, p) => acc + (s.inventory[p.id] ?? 0), 0);
    if (totalInv >= units && units > 0) {
      let need = units;
      let value = 0;
      for (const p of [...act].sort((a, b) => (s.inventory[b.id] ?? 0) - (s.inventory[a.id] ?? 0))) {
        const take = Math.min(need, s.inventory[p.id] ?? 0);
        s.inventory[p.id] -= take;
        value += take * blendedAsp(p) * (1 - wh.discountOffRetail);
        need -= take;
        if (need <= 0) break;
      }
      wh.receivables += value;
      wh.nextCollectionDay = Math.max(wh.nextCollectionDay, day + 60);
      wh.contractRetailers = Math.min(60, wh.contractRetailers + 2);
      addNews(s, 'finance', 1, 'Wholesale order booked', `${units.toLocaleString()} units → ${money(value)} receivable (net-60).`);
    } else {
      addNews(s, 'supply', 2, 'Wholesale order missed — insufficient inventory', `Retailers wanted ~${units.toLocaleString()} units. Stockouts damage retail relationships.`);
      wh.contractRetailers = Math.max(2, wh.contractRetailers - 1);
    }
    wh.quarterlyDemandUnits = Math.round(wh.quarterlyDemandUnits * 1.06);
  }

  s.finance.qAccum = emptyAccum();
}

function averageQualityApprox(s: GameState): number {
  const act = activeProducts(s);
  if (!act.length) return 55;
  return act.reduce((a, p) => a + p.quality, 0) / act.length;
}

function maybeExitOffer(s: GameState): void {
  if (s.gameOver || s.exitOffer) return;
  if (s.time.day < TUNING.exitOfferFirstDay) return;
  if ((s.eventCooldowns['__exit_offer'] ?? 0) > s.time.day) return;
  if (s.time.day % 30 !== 0) return;

  const last90 = s.history.daily.slice(-90).reduce((a, d) => a + d.rev, 0);
  if (last90 < 900_000) return;

  const last30 = s.history.daily.slice(-30).reduce((a, d) => a + d.rev, 0);
  const prior60 = s.history.daily.slice(-90, -30).reduce((a, d) => a + d.rev, 0);
  const growth = prior60 > 0 ? last30 / (prior60 / 2) - 1 : 0.5;
  const multiple = clamp(
    1.4 + growth * 2.2 + s.player.reputation / 200 + (s.finance.qAccum.netIncome > 0 ? 0.6 : 0),
    1.2,
    4.5,
  );
  const valuation = Math.max(250_000, Math.round((last90 * multiple) / 10_000) * 10_000);
  const acquirers = ['Tempur-Somnus Group', 'Legacy Sleep Corp', 'Horizon PE Partners', 'Sertur Sealy Holdings', 'Nordic Rest AB'];
  s.exitOffer = {
    acquirer: acquirers[s.time.day % acquirers.length],
    valuation,
    multiple: Math.round(multiple * 10) / 10,
    day: s.time.day,
  };
  addNews(s, 'opportunity', 3, `Acquisition interest: ${s.exitOffer.acquirer}`, `Indicative offer ~${money(valuation)} (${multiple.toFixed(1)}× trailing revenue). See Finance → Exit.`);
}

function checkAchievements(s: GameState, soldToday: boolean): void {
  const grant = (id: string) => {
    if (s.achievements.includes(id)) return;
    s.achievements.push(id);
    const def = ACHIEVEMENTS.find((a) => a.id === id);
    if (def) addNews(s, 'milestone', 1, `Achievement unlocked: ${def.name}`, def.desc);
  };
  if (soldToday) grant('first-sale');
  if (s.stats.totalRevenue >= 100_000) grant('first-100k');
  if (s.stats.totalRevenue >= 1_000_000) grant('first-1m');
  if (s.stats.totalRevenue >= 10_000_000) grant('first-10m');
  if (s.stats.quartersProfitable >= 1) grant('profitable-quarter');
}

function addNews(
  s: GameState,
  category: NewsItem['category'],
  severity: NewsItem['severity'],
  headline: string,
  detail?: string,
): void {
  s.news.unshift({
    id: `n_${s.time.day}_${s.news.length}_${Math.floor(Math.random() * 1e4).toString(36)}`,
    day: s.time.day,
    category,
    severity,
    headline,
    detail,
  });
  if (s.news.length > 140) s.news.length = 140;
}

/**
 * Offline catch-up: 1 in-game day per minute of wall-clock absence,
 * capped at TUNING.offlineMaxDays. Events are suppressed; any modal
 * decision is auto-skipped (effects without choices were applied).
 */
export function simulateAway(s: GameState, elapsedMs: number): number {
  if (elapsedMs < 3 * 60_000) return 0;
  const awayDays = Math.min(TUNING.offlineMaxDays, Math.floor(elapsedMs / 60_000));
  for (let i = 0; i < awayDays; i++) {
    tickDay(s, { suppressEvents: true });
    if (s.gameOver) break;
  }
  s.meta.lastPlayed = Date.now();
  return awayDays;
}
